/**
 * useMergedActions — thin useMemo wrapper around mergeActions.
 *
 * Accepts the raw actions array from the API and returns the merged view-layer
 * representation. Enriched data is sourced directly from action.enriched_data;
 * pass an explicit enrichedDataMap to override on a per-ID basis.
 */

import { useMemo } from 'react';

import type { EnrichedActionData, ProposedAction } from '../core/types';
import { mergeActions, type MergedOrOriginal } from '../utils/actionMerging';

export type { MergedOrOriginal } from '../utils/actionMerging';

interface UseMergedActionsResult {
  /** Merged representation of the actions */
  mergedItems: MergedOrOriginal[];
}

/**
 * Returns a stable merged view of the given actions.
 *
 * @param actions         Raw ProposedAction[] from the API (must be in execution_order).
 * @param enrichedDataMap Optional override map of action.id → EnrichedActionData.
 *                        When absent, action.enriched_data is used for each action.
 */
export function useMergedActions(
  actions: ProposedAction[],
  enrichedDataMap?: Record<number, EnrichedActionData>
): UseMergedActionsResult {
  const mergedItems = useMemo(
    () => mergeActions(actions, enrichedDataMap),
    [actions, enrichedDataMap]
  );

  return { mergedItems };
}
